import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import cn from 'classnames';
import { sortBy } from 'lodash';
import makeAnimated from 'react-select/animated';

import { FormControl, InputGroup } from 'react-bootstrap';

import { injectIntl } from 'react-intl';
import { GoSearch } from 'react-icons/go';

import { Map, List } from 'immutable';
import styles from './filter.scss';
import { categoryOptions, cityOptions, countryOptions, languageOptions, regionOptions, venueOptions } from './util';

const propTypes = {
  settings: PropTypes.instanceOf(Map).isRequired,
};

const translatedEventText = (intl, settings, id, textKey) => {
  if (settings.get('autoTranslateEvent') || settings.get('autoTranslateEventSearchFilters')) {
    return intl.formatMessage({
      id,
      defaultMessage: settings.get(textKey),
    });
  }
  return settings.get(textKey);
};
class Filters extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: true,
    };
  }

  async componentDidMount() {
    const { settings, getTicketSpotEvents } = this.props;
    if (settings.get('allowSearchPastLimits')) {
      getTicketSpotEvents();
    }
  }

  handleFilterClick = () => {
    this.setState({
      visible: !this.state.visible,
    });
  };

  render() {
    const { settings, events, filterEvents, intl } = this.props;
    const someFiltersEnabled =
      settings.get('showLanguageSearchFilter') ||
      settings.get('showUserSearch') ||
      settings.get('showVenueRegionSearchFilter') ||
      settings.get('showCountrySearchFilter') ||
      settings.get('showVenueSearchFilter') ||
      settings.get('showMonthSearchFilter') ||
      settings.get('showWeekSearchFilter') ||
      settings.get('showVenueCitySearchFilter') ||
      settings.get('showCategorySearchFilter');
    return (
      <div className={styles.filterContainer}>
        {settings.get('showUserSearch') && (
          <SearchBar settings={settings} filterEvents={filterEvents} intl={intl} onFilterClicked={this.handleFilterClick} />
        )}
        {someFiltersEnabled && (
          <SearchFilters visible={this.state.visible} settings={settings} events={events} filterEvents={filterEvents} intl={intl} />
        )}
      </div>
    );
  }
}

function SearchBar({ settings, filterEvents, intl }) {
  const placeholder = translatedEventText(intl, settings, 'search', 'searchbarText');
  return (
    <div className={styles.filters}>
      <InputGroup className="mb-3">
        {settings.get('showUserSearch') && (
          <InputGroup.Text className={styles.searchIcon}>
            <GoSearch className="user-select-icon" />
          </InputGroup.Text>
        )}
        {settings.get('showUserSearch') && (
          <FormControl
            placeholder={placeholder}
            aria-label={placeholder}
            aria-describedby="basic-addon1"
            className={cn(styles.search, 'user-select-search')}
            onChange={(val) => {
              filterEvents('name', val.target.value ? [{ value: val.target.value }] : []);
            }}
          />
        )}
      </InputGroup>
    </div>
  );
}

function SearchFilters({ settings, events, intl, filterEvents, visible }) {
  const LANGUAGES_LABELS = {
    en: intl.formatMessage({ id: 'english', defaultMessage: 'English' }),
    nl: intl.formatMessage({ id: 'dutch', defaultMessage: 'Dutch' }),
    fr: intl.formatMessage({ id: 'french', defaultMessage: 'French' }),
    de: intl.formatMessage({ id: 'german', defaultMessage: 'German' }),
    it: intl.formatMessage({ id: 'italian', defaultMessage: 'Italian' }),
  };

  const MONTHS = List([
    intl.formatMessage({ id: 'january', defaultMessage: 'January' }),
    intl.formatMessage({ id: 'february', defaultMessage: 'February' }),
    intl.formatMessage({ id: 'march', defaultMessage: 'March' }),
    intl.formatMessage({ id: 'april', defaultMessage: 'April' }),
    intl.formatMessage({ id: 'may', defaultMessage: 'May' }),
    intl.formatMessage({ id: 'june', defaultMessage: 'June' }),
    intl.formatMessage({ id: 'july', defaultMessage: 'July' }),
    intl.formatMessage({ id: 'august', defaultMessage: 'August' }),
    intl.formatMessage({ id: 'september', defaultMessage: 'September' }),
    intl.formatMessage({ id: 'october', defaultMessage: 'October' }),
    intl.formatMessage({ id: 'november', defaultMessage: 'November' }),
    intl.formatMessage({ id: 'december', defaultMessage: 'December' }),
  ]);
  const WEEKS = List([
    intl.formatMessage({ id: 'monday', defaultMessage: 'Monday' }),
    intl.formatMessage({ id: 'tuesday', defaultMessage: 'Tuesday' }),
    intl.formatMessage({ id: 'wednesday', defaultMessage: 'Wednesday' }),
    intl.formatMessage({ id: 'thursday', defaultMessage: 'Thursday' }),
    intl.formatMessage({ id: 'friday', defaultMessage: 'Friday' }),
    intl.formatMessage({ id: 'saturday', defaultMessage: 'Saturday' }),
    intl.formatMessage({ id: 'sunday', defaultMessage: 'Sunday' }),
  ]);

  const MONTHS_OPTIONS = MONTHS.map((month, index) => Map({ label: month, value: index }));
  const WEEKS_OPTIONS = WEEKS.map((week, index) => Map({ label: week, value: index }));

  const noOptionsMessage = intl.formatMessage({
    id: 'no-options',
    defaultMessage: 'No options',
  });
  return (
    <div className={styles.filters}>
      {visible && (
        <div
          className={cn(styles.searchFilters, {
            [styles.filterMobile]: window.isMobileType,
          })}
        >
          <FilterSelection
            events={events}
            noOptionsMessage={noOptionsMessage}
            enabled={settings.get('showCategorySearchFilter')}
            filterType="category"
            placeholder={translatedEventText(intl, settings, 'select-category', 'searchFilterCategoryText')}
            filterEvents={filterEvents}
            optionBuilder={categoryOptions}
          >
            {intl.formatMessage({ id: 'category', defaultMessage: 'Category' })}
          </FilterSelection>
          <FilterSelection
            events={events}
            enabled={settings.get('showLanguageSearchFilter')}
            filterType="locale"
            placeholder={translatedEventText(intl, settings, 'select-language', 'searchFilterLanguageText')}
            noOptionsMessage={noOptionsMessage}
            filterEvents={filterEvents}
            optionBuilder={languageOptions(LANGUAGES_LABELS)}
          >
            {intl.formatMessage({ id: 'language', defaultMessage: 'Language' })}
          </FilterSelection>
          <FilterSelection
            enabled={settings.get('showCountrySearchFilter')}
            events={events}
            noOptionsMessage={noOptionsMessage}
            filterType="country"
            placeholder={translatedEventText(intl, settings, 'select-country', 'searchFilterCountryText')}
            filterEvents={filterEvents}
            optionBuilder={countryOptions}
          >
            {intl.formatMessage({ id: 'country', defaultMessage: 'Country' })}
          </FilterSelection>
          <FilterSelection
            enabled={settings.get('showVenueSearchFilter')}
            events={events}
            noOptionsMessage={noOptionsMessage}
            filterType="venue"
            placeholder={translatedEventText(intl, settings, 'select-venue', 'searchFilterVenueText')}
            filterEvents={filterEvents}
            optionBuilder={venueOptions}
          >
            {intl.formatMessage({ id: 'venue', defaultMessage: 'Venue' })}
          </FilterSelection>
          <FilterSelection
            enabled={settings.get('showVenueRegionSearchFilter')}
            events={events}
            noOptionsMessage={noOptionsMessage}
            filterType="region"
            placeholder={translatedEventText(intl, settings, 'select-region', 'searchFilterRegionText')}
            filterEvents={filterEvents}
            optionBuilder={regionOptions}
          >
            {intl.formatMessage({ id: 'venue', defaultMessage: 'Venue' })}
          </FilterSelection>
          <FilterSelection
            enabled={settings.get('showVenueCitySearchFilter')}
            events={events}
            noOptionsMessage={noOptionsMessage}
            filterType="city"
            placeholder={translatedEventText(intl, settings, 'select-city', 'searchFilterCityText')}
            filterEvents={filterEvents}
            optionBuilder={cityOptions}
          >
            {intl.formatMessage({ id: 'venue', defaultMessage: 'Venue' })}
          </FilterSelection>
          <FilterSelection
            events={events}
            noOptionsMessage={noOptionsMessage}
            enabled={settings.get('showMonthSearchFilter')}
            filterType="month"
            placeholder={translatedEventText(intl, settings, 'select-month', 'searchFilterMonthText')}
            filterEvents={filterEvents}
            options={MONTHS_OPTIONS}
          >
            {intl.formatMessage({ id: 'month', defaultMessage: 'Month' })}
          </FilterSelection>
          <FilterSelection
            events={events}
            noOptionsMessage={noOptionsMessage}
            enabled={settings.get('showWeekSearchFilter')}
            filterType="week"
            placeholder={translatedEventText(intl, settings, 'select-week', 'searchFilterWeekText')}
            filterEvents={filterEvents}
            options={WEEKS_OPTIONS}
          >
            {intl.formatMessage({ id: 'week', defaultMessage: 'Week' })}
          </FilterSelection>
        </div>
      )}
    </div>
  );
}

function FilterSelection({ filterEvents, events, placeholder, filterType, options, optionBuilder, enabled, noOptionsMessage }) {
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (base) => ({
      ...base,
      flex: 1,
      zIndex: 1000,
      marginRight: 20,
      borderRadius: 15,
      marginTop: window.isMobileType ? 5 : 0,
      maxWidth: window.isMobileType ? '100%' : 250,
    }),
  };

  if (!enabled) return <span />;
  const animatedComponents = makeAnimated();

  const opts = optionBuilder ? optionBuilder(events) : options;

  return (
    <Select
      menuPortalTarget={document.body}
      styles={selectStyles}
      isMulti
      noOptionsMessage={() => noOptionsMessage}
      className={cn('user-multi-select')}
      classNamePrefix="user-select"
      components={animatedComponents}
      options={sortBy(opts.toJS(), 'value')}
      placeholder={`${placeholder}...`}
      onChange={(vals) => filterEvents(filterType, vals)}
    />
  );
}

Filters.propTypes = propTypes;

export default injectIntl(Filters);
